import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Snackbar,
  Tooltip,
  InputAdornment,
  Checkbox,
  Chip,
} from '@mui/material';
import {
  Search as SearchIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  AttachMoney as MoneyIcon,
  Person as PersonIcon,
  Phone as PhoneIcon,
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import { apiService } from '../services/api';
import { getCurrentMonthYearBrasilia } from '../utils/dateUtils';

interface RefundOrder {
  id: string;
  orderNumber: string;
  value: string;
  refundType: string;
  refundKey: string;
  status: string;
  reason: string;
  clientName: string;
  clientPhone: string;
  createdAt: string;
  updatedAt: string;
}

interface NewRefundOrder {
  orderNumber: string;
  value: string;
  refundType: string;
  refundKey: string;
  status: string;
  reason: string;
  clientName: string;
  clientPhone: string;
}

type PeriodType = 'month' | 'day';

const RefundOrders: React.FC = () => {
  const { user: currentUser } = useAuth();
  const { month: currentMonth, year: currentYear } = getCurrentMonthYearBrasilia();
  const today = new Date().toISOString().slice(0, 10);
  const [refunds, setRefunds] = useState<RefundOrder[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [openAddDialog, setOpenAddDialog] = useState(false);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [editingRefund, setEditingRefund] = useState<RefundOrder | null>(null);
  const [selectedRefunds, setSelectedRefunds] = useState<Set<string>>(new Set());
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error' | 'warning' | 'info',
  });
  const [stats, setStats] = useState({
    count: 0,
    totalValue: '0.00',
    byType: {},
    byStatus: {},
    filters: {
      refundType: 'ALL',
      month: null,
      year: null,
    },
  });

  const [periodType, setPeriodType] = useState<PeriodType>('month');
  const [month, setMonth] = useState(currentMonth.toString());
  const [year, setYear] = useState(currentYear.toString());
  const [selectedDate, setSelectedDate] = useState(today);

  const [newRefundData, setNewRefundData] = useState<NewRefundOrder>({
    orderNumber: '',
    value: '',
    refundType: 'PIX',
    refundKey: '',
    status: 'ESTORNAR',
    reason: '',
    clientName: '',
    clientPhone: '',
  });

  const canEdit = currentUser?.role === 'ADMIN' || currentUser?.role === 'SUPPORT' || currentUser?.role === 'MANAGER';

  const getDateRange = (): { startDate: string; endDate: string } => {
    if (periodType === 'day') {
      return { startDate: selectedDate, endDate: selectedDate };
    }
    const m = parseInt(month);
    const y = parseInt(year);
    const startDate = `${y}-${String(m).padStart(2, '0')}-01`;
    const lastDay = new Date(y, m, 0).getDate();
    const endDate = `${y}-${String(m).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;
    return { startDate, endDate };
  };

  const loadRefunds = async () => {
    try {
      setLoading(true);
      const { startDate, endDate } = getDateRange();
      const data = await apiService.getAllRefundOrders(startDate, endDate);
      setRefunds(data);
    } catch (error: any) {
      showSnackbar('Erro ao carregar estornos', 'error');
      console.error('Erro ao carregar estornos:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async (refundType?: string) => {
    try {
      const { startDate, endDate } = getDateRange();
      const data = await apiService.getRefundOrderStats(
        refundType,
        startDate,
        endDate
      );
      setStats(data);
    } catch (error: any) {
      console.error('Erro ao carregar estatísticas:', error);
    }
  };

  useEffect(() => {
    loadRefunds();
    loadStats();
  }, [periodType, month, year, selectedDate]);

  useEffect(() => {
    loadStats(typeFilter || undefined);
  }, [typeFilter, refunds]);

  const showSnackbar = (message: string, severity: 'success' | 'error' | 'warning' | 'info') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // Adicionar novo estorno
  const handleAddRefund = async () => {
    try {
      if (!newRefundData.orderNumber || !newRefundData.value || !newRefundData.refundType || !newRefundData.refundKey || !newRefundData.status || !newRefundData.reason || !newRefundData.clientName || !newRefundData.clientPhone) {
        showSnackbar('Preencha todos os campos obrigatórios', 'warning');
        return;
      }

      await apiService.createRefundOrder(newRefundData);
      showSnackbar('Estorno adicionado com sucesso!', 'success');
      setOpenAddDialog(false);
      setNewRefundData({
        orderNumber: '',
        value: '',
        refundType: 'PIX',
        refundKey: '',
        status: 'ESTORNAR',
        reason: '',
        clientName: '',
        clientPhone: '',
      });
      loadRefunds();
    } catch (error: any) {
      showSnackbar('Erro ao adicionar estorno', 'error');
      console.error('Erro ao adicionar estorno:', error);
    }
  };

  // Editar estorno
  const handleEditClick = (refund: RefundOrder) => {
    setEditingRefund(refund);
    setOpenEditDialog(true);
  };

  const handleUpdateRefund = async () => {
    if (!editingRefund) return;

    try {
      await apiService.updateRefundOrder(editingRefund.id, {
        orderNumber: editingRefund.orderNumber,
        value: editingRefund.value,
        refundType: editingRefund.refundType,
        refundKey: editingRefund.refundKey,
        status: editingRefund.status,
        reason: editingRefund.reason,
        clientName: editingRefund.clientName,
        clientPhone: editingRefund.clientPhone,
      });

      showSnackbar('Estorno atualizado com sucesso!', 'success');
      setOpenEditDialog(false);
      setEditingRefund(null);
      loadRefunds();
    } catch (error: any) {
      showSnackbar('Erro ao atualizar estorno', 'error');
      console.error('Erro ao atualizar estorno:', error);
    }
  };

  // Deletar estorno
  const handleDeleteRefund = async (id: string) => {
    if (!window.confirm('Tem certeza que deseja deletar este estorno?')) {
      return;
    }

    try {
      await apiService.deleteRefundOrder(id);
      showSnackbar('Estorno deletado com sucesso!', 'success');
      loadRefunds();
    } catch (error: any) {
      showSnackbar('Erro ao deletar estorno', 'error');
      console.error('Erro ao deletar estorno:', error);
    }
  };

  // Deletar múltiplos estornos
  const handleDeleteMultiple = async () => {
    if (selectedRefunds.size === 0) {
      showSnackbar('Selecione pelo menos um estorno', 'warning');
      return;
    }

    if (!window.confirm(`Tem certeza que deseja deletar ${selectedRefunds.size} estornos?`)) {
      return;
    }

    try {
      await apiService.deleteMultipleRefundOrders(Array.from(selectedRefunds));
      showSnackbar(`${selectedRefunds.size} estornos deletados com sucesso!`, 'success');
      setSelectedRefunds(new Set());
      loadRefunds();
    } catch (error: any) {
      showSnackbar('Erro ao deletar estornos', 'error');
      console.error('Erro ao deletar estornos:', error);
    }
  };

  // Atualizar status diretamente na tabela
  const handleStatusChange = async (id: string, newStatus: string) => {
    try {
      await apiService.updateRefundOrder(id, { status: newStatus });
      showSnackbar('Status atualizado com sucesso!', 'success');
      loadRefunds();
    } catch (error: any) {
      showSnackbar('Erro ao atualizar status', 'error');
      console.error('Erro ao atualizar status:', error);
    }
  };

  // Seleção de múltiplos
  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      const allIds = new Set(filteredRefunds.map(refund => refund.id));
      setSelectedRefunds(allIds);
    } else {
      setSelectedRefunds(new Set());
    }
  };

  const handleSelectOne = (id: string) => {
    const newSelected = new Set(selectedRefunds);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedRefunds(newSelected);
  };

  // Filtros
  const filteredRefunds = refunds.filter(refund => {
    const matchesSearch = 
      refund.orderNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
      refund.clientPhone.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesType = !typeFilter || refund.refundType === typeFilter;
    const matchesStatus = !statusFilter || refund.status === statusFilter;

    return matchesSearch && matchesType && matchesStatus;
  });

  // Mapear tipos e status para exibição
  const typeMap: { [key: string]: string } = {
    'PIX': 'Pix',
    'CARTAO': 'Cartão',
  };

  const statusMap: { [key: string]: string } = {
    'ESTORNADO': 'Estornado',
    'ESTORNAR': 'Estornar',
  };

  const typeOptions = ['PIX', 'CARTAO'];
  const statusOptions = ['ESTORNADO', 'ESTORNAR'];

  // Cores por status
  const statusColors: { [key: string]: string } = {
    'ESTORNADO': '#10b981', // Verde
    'ESTORNAR': '#f59e0b',  // Amarelo
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, color: '#1a1a1a', mb: 1 }}>
            Pedidos Estornados
          </Typography>
          <Typography variant="body2" sx={{ color: '#666' }}>
            Gerencie e acompanhe estornos por tipo de pagamento
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>Período</InputLabel>
            <Select
              value={periodType}
              onChange={(e) => setPeriodType(e.target.value as PeriodType)}
              label="Período"
            >
              <MenuItem value="month">Mês inteiro</MenuItem>
              <MenuItem value="day">Dia específico</MenuItem>
            </Select>
          </FormControl>

          {periodType === 'month' && (
            <>
              <FormControl size="small" sx={{ minWidth: 120 }}>
                <InputLabel>Mês</InputLabel>
                <Select value={month} onChange={(e) => setMonth(e.target.value)} label="Mês">
                  <MenuItem value="1">Janeiro</MenuItem>
                  <MenuItem value="2">Fevereiro</MenuItem>
                  <MenuItem value="3">Março</MenuItem>
                  <MenuItem value="4">Abril</MenuItem>
                  <MenuItem value="5">Maio</MenuItem>
                  <MenuItem value="6">Junho</MenuItem>
                  <MenuItem value="7">Julho</MenuItem>
                  <MenuItem value="8">Agosto</MenuItem>
                  <MenuItem value="9">Setembro</MenuItem>
                  <MenuItem value="10">Outubro</MenuItem>
                  <MenuItem value="11">Novembro</MenuItem>
                  <MenuItem value="12">Dezembro</MenuItem>
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 100 }}>
                <InputLabel>Ano</InputLabel>
                <Select value={year} onChange={(e) => setYear(e.target.value)} label="Ano">
                  <MenuItem value="2023">2023</MenuItem>
                  <MenuItem value="2024">2024</MenuItem>
                  <MenuItem value="2025">2025</MenuItem>
                  <MenuItem value="2026">2026</MenuItem>
                </Select>
              </FormControl>
            </>
          )}

          {periodType === 'day' && (
            <TextField
              size="small"
              label="Data"
              type="date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ minWidth: 160 }}
            />
          )}

          {canEdit && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setOpenAddDialog(true)}
              sx={{
                backgroundColor: '#D4AF37',
                color: '#000',
                fontWeight: 600,
                px: 3,
                py: 1.5,
                '&:hover': {
                  backgroundColor: '#B8941F',
                },
              }}
            >
              Adicionar Estorno
            </Button>
          )}
        </Box>
      </Box>

      {/* Estatísticas */}
      <Card sx={{ mb: 3, backgroundColor: '#f8f9fa', border: '1px solid #e0e0e0' }}>
        <CardContent>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="body2" sx={{ color: '#666', mb: 0.5 }}>
                Total de Estornos
              </Typography>
              <Typography variant="h5" sx={{ fontWeight: 700, color: '#1a1a1a' }}>
                {stats.count}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="body2" sx={{ color: '#666', mb: 0.5 }}>
                Valor Total
              </Typography>
              <Typography variant="h5" sx={{ fontWeight: 700, color: '#dc2626' }}>
                {Number(stats.totalValue).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="body2" sx={{ color: '#666', mb: 0.5 }}>
                Estornos Pix
              </Typography>
              <Typography variant="h5" sx={{ fontWeight: 600, color: '#8b5cf6' }}>
                {Number((stats.byType as any)?.PIX?.total ?? 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="body2" sx={{ color: '#666', mb: 0.5 }}>
                Estornos Cartão
              </Typography>
              <Typography variant="h5" sx={{ fontWeight: 600, color: '#3b82f6' }}>
                {Number((stats.byType as any)?.CARTAO?.total ?? 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
              </Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Filtros */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                placeholder="Buscar por número de pedido ou telefone"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <FormControl fullWidth>
                <InputLabel>Tipo de Estorno</InputLabel>
                <Select
                  value={typeFilter}
                  onChange={(e) => setTypeFilter(e.target.value)}
                  label="Tipo de Estorno"
                >
                  <MenuItem value="">Todos</MenuItem>
                  {typeOptions.map(type => (
                    <MenuItem key={type} value={type}>
                      {typeMap[type]}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <FormControl fullWidth>
                <InputLabel>Situação</InputLabel>
                <Select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  label="Situação"
                >
                  <MenuItem value="">Todas</MenuItem>
                  {statusOptions.map(status => (
                    <MenuItem key={status} value={status}>
                      {statusMap[status]}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={loadRefunds}
                sx={{
                  borderColor: '#D4AF37',
                  color: '#D4AF37',
                  height: '56px',
                  '&:hover': {
                    borderColor: '#B8941F',
                    backgroundColor: 'rgba(212, 175, 55, 0.04)',
                  },
                }}
              >
                Atualizar
              </Button>
            </Grid>
            {canEdit && selectedRefunds.size > 0 && (
              <Grid item xs={12} md={2}>
                <Button
                  fullWidth
                  variant="contained"
                  color="error"
                  startIcon={<DeleteIcon />}
                  onClick={handleDeleteMultiple}
                  sx={{ height: '56px' }}
                >
                  Deletar ({selectedRefunds.size})
                </Button>
              </Grid>
            )}
          </Grid>
        </CardContent>
      </Card>

      {/* Tabela */}
      <Card>
        <TableContainer sx={{ overflowX: 'auto', width: '100%', display: 'block' }}>
          <Table sx={{ width: '100%', tableLayout: 'auto' }}>
            <TableHead>
              <TableRow sx={{ backgroundColor: 'rgba(212, 175, 55, 0.05)', height: 70, '& > th': { py: 3 } }}>
                {canEdit && (
                  <TableCell sx={{ width: 50, textAlign: 'center', px: 1 }}>
                    <Checkbox
                      checked={selectedRefunds.size === filteredRefunds.length && filteredRefunds.length > 0}
                      indeterminate={selectedRefunds.size > 0 && selectedRefunds.size < filteredRefunds.length}
                      onChange={handleSelectAll}
                    />
                  </TableCell>
                )}
                <TableCell sx={{ fontWeight: 600, color: '#D4AF37', px: 1, flex: 1 }}>Nº PEDIDO</TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#D4AF37', px: 1, flex: 1 }}>VALOR</TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#D4AF37', px: 1, flex: 1 }}>TIPO DE ESTORNO</TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#D4AF37', px: 1, flex: 1 }}>CHAVE</TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#D4AF37', px: 1, flex: 1 }}>SITUAÇÃO</TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#D4AF37', px: 1, flex: 1 }}>MOTIVO</TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#D4AF37', px: 1, flex: 1 }}>CLIENTE</TableCell>
                {canEdit && (
                  <TableCell sx={{ fontWeight: 600, color: '#D4AF37', px: 1, textAlign: 'center', flex: 1 }}>AÇÕES</TableCell>
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={canEdit ? 8 : 6} align="center" sx={{ py: 4 }}>
                    Carregando...
                  </TableCell>
                </TableRow>
              ) : filteredRefunds.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={canEdit ? 8 : 6} align="center" sx={{ py: 4 }}>
                    Nenhum estorno encontrado
                  </TableCell>
                </TableRow>
              ) : (
                filteredRefunds.map((refund) => (
                  <TableRow
                    key={refund.id}
                    sx={{
                      '&:hover': { backgroundColor: 'rgba(212, 175, 55, 0.02)' },
                      height: 70,
                    }}
                  >
                    {canEdit && (
                      <TableCell sx={{ textAlign: 'center', px: 1 }}>
                        <Checkbox
                          checked={selectedRefunds.has(refund.id)}
                          onChange={() => handleSelectOne(refund.id)}
                        />
                      </TableCell>
                    )}
                    <TableCell sx={{ px: 1, flex: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {refund.orderNumber}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ px: 1, flex: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <MoneyIcon sx={{ fontSize: 18, color: '#10b981' }} />
                        <Typography variant="body2" sx={{ fontWeight: 500, color: '#10b981' }}>
                          R$ {refund.value}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell sx={{ px: 1, flex: 1 }}>
                      <Chip
                        label={typeMap[refund.refundType] || refund.refundType}
                        size="small"
                        sx={{
                          backgroundColor: refund.refundType === 'PIX' ? '#8b5cf6' : '#3b82f6',
                          color: '#fff',
                          fontWeight: 600,
                        }}
                      />
                    </TableCell>
                    <TableCell sx={{ px: 1, flex: 1 }}>
                      <Typography variant="body2">
                        {refund.refundKey || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ px: 1, flex: 1 }}>
                      <FormControl size="small" fullWidth>
                        <Select
                          value={refund.status}
                          onChange={(e) => handleStatusChange(refund.id, e.target.value)}
                          sx={{
                            backgroundColor: statusColors[refund.status],
                            color: '#fff',
                            fontWeight: 600,
                            '& .MuiSelect-icon': { color: '#fff' },
                            '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
                          }}
                        >
                          {statusOptions.map(status => (
                            <MenuItem key={status} value={status}>
                              {statusMap[status]}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </TableCell>
                    <TableCell sx={{ px: 1, flex: 1 }}>
                      <Tooltip title={refund.reason}>
                        <Typography variant="body2" sx={{ 
                          maxWidth: 200, 
                          overflow: 'hidden', 
                          textOverflow: 'ellipsis', 
                          whiteSpace: 'nowrap' 
                        }}>
                          {refund.reason}
                        </Typography>
                      </Tooltip>
                    </TableCell>
                    <TableCell sx={{ px: 1, flex: 1 }}>
                      <Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <PersonIcon sx={{ fontSize: 16, color: '#666' }} />
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {refund.clientName}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                          <PhoneIcon sx={{ fontSize: 14, color: '#666' }} />
                          <Typography variant="caption" sx={{ color: '#666' }}>
                            {refund.clientPhone}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    {canEdit && (
                      <TableCell sx={{ px: 1, textAlign: 'center', flex: 1 }}>
                        <Tooltip title="Editar">
                          <IconButton
                            size="small"
                            onClick={() => handleEditClick(refund)}
                            sx={{ color: '#D4AF37', mr: 1 }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Deletar">
                          <IconButton
                            size="small"
                            onClick={() => handleDeleteRefund(refund.id)}
                            sx={{ color: '#ef4444' }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    )}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>

      {/* Dialog: Adicionar Estorno */}
      <Dialog open={openAddDialog} onClose={() => setOpenAddDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ backgroundColor: '#D4AF37', color: '#000', fontWeight: 600 }}>
          Adicionar Novo Estorno
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Número do Pedido"
                value={newRefundData.orderNumber}
                onChange={(e) => setNewRefundData({ ...newRefundData, orderNumber: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Valor"
                value={newRefundData.value}
                onChange={(e) => setNewRefundData({ ...newRefundData, value: e.target.value })}
                placeholder="0.00"
                required
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth required>
                <InputLabel>Tipo de Estorno</InputLabel>
                <Select
                  value={newRefundData.refundType}
                  onChange={(e) => setNewRefundData({ ...newRefundData, refundType: e.target.value })}
                  label="Tipo de Estorno"
                >
                  {typeOptions.map(type => (
                    <MenuItem key={type} value={type}>
                      {typeMap[type]}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Chave"
                value={newRefundData.refundKey}
                onChange={(e) => setNewRefundData({ ...newRefundData, refundKey: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth required>
                <InputLabel>Situação</InputLabel>
                <Select
                  value={newRefundData.status}
                  onChange={(e) => setNewRefundData({ ...newRefundData, status: e.target.value })}
                  label="Situação"
                >
                  {statusOptions.map(status => (
                    <MenuItem key={status} value={status}>
                      {statusMap[status]}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Cliente"
                value={newRefundData.clientName}
                onChange={(e) => setNewRefundData({ ...newRefundData, clientName: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Telefone"
                value={newRefundData.clientPhone}
                onChange={(e) => setNewRefundData({ ...newRefundData, clientPhone: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Motivo"
                value={newRefundData.reason}
                onChange={(e) => setNewRefundData({ ...newRefundData, reason: e.target.value })}
                multiline
                rows={3}
                required
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setOpenAddDialog(false)} sx={{ color: '#666' }}>
            Cancelar
          </Button>
          <Button
            onClick={handleAddRefund}
            variant="contained"
            sx={{
              backgroundColor: '#D4AF37',
              color: '#000',
              '&:hover': { backgroundColor: '#B8941F' },
            }}
          >
            Adicionar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog: Editar Estorno */}
      <Dialog open={openEditDialog} onClose={() => setOpenEditDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ backgroundColor: '#D4AF37', color: '#000', fontWeight: 600 }}>
          Editar Estorno
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          {editingRefund && (
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Número do Pedido"
                  value={editingRefund.orderNumber}
                  onChange={(e) => setEditingRefund({ ...editingRefund, orderNumber: e.target.value })}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Valor"
                  value={editingRefund.value}
                  onChange={(e) => setEditingRefund({ ...editingRefund, value: e.target.value })}
                  placeholder="0.00"
                  required
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth required>
                  <InputLabel>Tipo de Estorno</InputLabel>
                  <Select
                    value={editingRefund.refundType}
                    onChange={(e) => setEditingRefund({ ...editingRefund, refundType: e.target.value })}
                    label="Tipo de Estorno"
                  >
                    {typeOptions.map(type => (
                      <MenuItem key={type} value={type}>
                        {typeMap[type]}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="Chave"
                  value={editingRefund.refundKey}
                  onChange={(e) => setEditingRefund({ ...editingRefund, refundKey: e.target.value })}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth required>
                  <InputLabel>Situação</InputLabel>
                  <Select
                    value={editingRefund.status}
                    onChange={(e) => setEditingRefund({ ...editingRefund, status: e.target.value })}
                    label="Situação"
                  >
                    {statusOptions.map(status => (
                      <MenuItem key={status} value={status}>
                        {statusMap[status]}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Cliente"
                  value={editingRefund.clientName}
                  onChange={(e) => setEditingRefund({ ...editingRefund, clientName: e.target.value })}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Telefone"
                  value={editingRefund.clientPhone}
                  onChange={(e) => setEditingRefund({ ...editingRefund, clientPhone: e.target.value })}
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Motivo"
                  value={editingRefund.reason}
                  onChange={(e) => setEditingRefund({ ...editingRefund, reason: e.target.value })}
                  multiline
                  rows={3}
                  required
                />
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setOpenEditDialog(false)} sx={{ color: '#666' }}>
            Cancelar
          </Button>
          <Button
            onClick={handleUpdateRefund}
            variant="contained"
            sx={{
              backgroundColor: '#D4AF37',
              color: '#000',
              '&:hover': { backgroundColor: '#B8941F' },
            }}
          >
            Salvar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default RefundOrders;
